// frontend/src/components/orcamento/OrcamentoProjecao.js
// Orçamento com Monte Carlo (22/10/2026): aba Orçamento (grid P50 editável de
// despesas e receitas + resultado + pilares ao vivo + painel da categoria) e aba
// Simulação (distribuição do resultado). Só para ORCAMENTO_PROJECAO_CLIENTES.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useProjecao, { categoriasDe } from './useProjecao';
import GridProjecao from './GridProjecao';
import PainelPilares from './PainelPilares';
import AnaliseCategoria from './AnaliseCategoria';
import AbaSimulacao from './AbaSimulacao';
import AbaPremissas, { PremissasConsolidado } from './AbaPremissas';
import ComoFunciona from './ComoFunciona';
import { notificar } from '../../utils/notify';
import '../../styles/projetosAdvantec.css';
import '../../styles/orcamentoProjecao.css';

export const ORCAMENTO_PROJECAO_CLIENTES = new Set(['25', '27']);
const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

// "A base já cobre 2026; escolha um ano posterior a 2026-09" → "09/2026"
const fimDoErro = (msg) => {
  const m = /posterior a (\d{4})-(\d{2})/.exec(String(msg || ''));
  return m ? `${m[2]}/${m[1]}` : null;
};

// 3480000 → 'R$ 3,48 mi'; abaixo de 1 mi → 'R$ 850 mil'; negativo com '−'
const fmtMi = (v) => {
  if (!Number.isFinite(v)) return '—';
  const s = v < 0 ? '−' : '';
  const a = Math.abs(v);
  return a >= 1e6
    ? `${s}R$ ${(a / 1e6).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mi`
    : `${s}R$ ${Math.round(a / 1e3).toLocaleString('pt-BR')} mil`;
};

// '2023-10' → '10/2023'
const mesAno = (ym) => (ym && /^\d{4}-\d{2}$/.test(ym)) ? `${ym.slice(5, 7)}/${ym.slice(0, 4)}` : (ym || '—');

const SECOES = [['despesa', 'Despesas'], ['receita', 'Receitas']];
const contarSelos = (cats) => cats.reduce((a, c) => ({ ...a, [c.selo]: (a[c.selo] || 0) + 1 }), {});

// contagem compacta por selo de uma natureza: "Despesas ●15 ●18 ●32 ●0"
function Selos({ titulo, selos }) {
  return (
    <span className="op-selos" title="Confiáveis · conferir · pouco previsíveis · decisão (detalhes em Como funciona)">
      {titulo}:
      {['verde', 'amarelo', 'vermelho', 'decisao'].map((s) => (
        <span key={s}><span className={`op-selo ${s}`}></span><b>{selos[s] || 0}</b></span>
      ))}
    </span>
  );
}

// casamento de pilar por NOME (mesma regra do backend: sem acento, sem caixa, espaços colapsados)
const chaveNome = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().split(/\s+/).filter(Boolean).join(' ');

// siglas curtas das empresas para os selos: 3 primeiras letras do nome; se repetir, o id
function siglasDe(clientes) {
  const base = (clientes || []).map((c) => ({ id: c.id, nome: c.nome, sigla: String(c.nome || c.id).replace(/[^A-Za-zÀ-ÿ0-9]/g, '').slice(0, 3).toUpperCase() }));
  const repetida = new Set(base.map((b) => b.sigla)).size !== base.length;
  return Object.fromEntries(base.map((b) => [b.id, { nome: b.nome, sigla: repetida || !b.sigla ? String(b.id) : b.sigla }]));
}

// Popover da categoria na visão consolidada: as origens e o atalho para abrir cada empresa
// (o painel de análise é por empresa; aqui não há histórico fundido para analisar)
function OrigensCategoria({ alvo, siglas, onAbrirEmpresa, onFechar }) {
  useEffect(() => {
    const tecla = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [onFechar]);
  const fmtR = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);
  const { cat, natureza } = alvo;
  return (
    <div className="op-origens-fundo" onClick={onFechar}>
      <div className="op-origens" role="dialog" aria-label={`Origens de ${cat.nome}`} onClick={(e) => e.stopPropagation()}>
        <div className="op-origens-head">
          <div>
            <div className="pa-eyebrow">{natureza === 'receita' ? 'Receita' : 'Despesa'} · visão consolidada</div>
            <b>{cat.nome}</b>
          </div>
          <button type="button" className="pa-icon-btn" onClick={onFechar} aria-label="Fechar"><i className="fas fa-times"></i></button>
        </div>
        {(cat.origens || []).map((o) => (
          <div key={`${o.cliente_id}-${o.codigo}`} className="op-origens-item">
            <span><span className="op-emp">{siglas?.[o.cliente_id]?.sigla || o.cliente_id}</span>
              {siglas?.[o.cliente_id]?.nome || o.cliente_id} · <span className="text-muted">{o.codigo}</span></span>
            <b>{fmtR(o.total_p50)}</b>
            <button type="button" className="pa-btn pa-btn-ghost" onClick={() => onAbrirEmpresa(o.cliente_id)}>
              Abrir na {siglas?.[o.cliente_id]?.nome || o.cliente_id}
            </button>
          </div>
        ))}
        <div className="op-prem-ctx mt-2">P50 do ano em cada empresa. A análise da categoria (histórico, outliers, calculadora) abre na projeção da empresa.</div>
      </div>
    </div>
  );
}

export default function OrcamentoProjecao({ clienteId, ano, onTrocarAno }) {
  const navigate = useNavigate();
  const [busca, setBusca] = useSearchParams();
  // visão consolidada do grupo: fica na URL (?consolidar=1) para sobreviver ao recarregar
  const consolidar = busca.get('consolidar') === '1';
  const alternarConsolidar = () => setBusca((sp) => {
    const n = new URLSearchParams(sp);
    if (consolidar) n.delete('consolidar'); else n.set('consolidar', '1');
    return n;
  });
  const abrirEmpresa = (id) => navigate(`/Orcamento_despesas/${id}?ano=${ano}`);
  const p = useProjecao(clienteId, ano, consolidar);
  const [origensAbertas, setOrigensAbertas] = useState(null);
  const fecharOrigens = useCallback(() => setOrigensAbertas(null), []);
  const [aba, setAba] = useState('orcamento');
  const [ajuda, setAjuda] = useState(false);
  const fecharAjuda = useCallback(() => setAjuda(false), []);
  // categoria aberta no painel: { natureza, codigo }
  const [catAberta, setCatAberta] = useState(null);
  const [versao, setVersao] = useState(0);
  const [nomesPilar, setNomesPilar] = useState({});
  const [realizadoPilar, setRealizadoPilar] = useState(null);
  const baseFim = p.proj?.base?.fim || '';

  // troca de cliente/ano/consolidado fecha o painel da categoria
  useEffect(() => { setCatAberta(null); setOrigensAbertas(null); }, [clienteId, ano, consolidar]);

  // nomes dos departamentos e realizado (receita e despesa) do ano anterior por pilar.
  // Se a base termina no próprio ano anterior, o realizado vai só até o fim da base
  // (o rodapé anualiza); senão, ano anterior inteiro.
  useEffect(() => {
    setNomesPilar({}); setRealizadoPilar(null);
    if (!clienteId || !baseFim) return;
    const [fimAno, fimMes] = baseFim.split('-').map(Number);
    const mesFim = fimAno === ano - 1 ? fimMes : 12;
    let vivo = true;
    // consolidado: o DRE também consolida e os pilares casam pelo NOME (chave normalizada)
    axios.get(`${API}/api/dre-completo-ytd/`, {
      params: { cliente_id: clienteId, ano: ano - 1, mes_ini: 1, mes_fim: mesFim, status: 'pago', ...(consolidar ? { consolidar: 1 } : {}) },
      headers: headers(), timeout: 120000,
    }).then((r) => {
      if (!vivo || !r.data?.success) return;
      const nomes = {}, real = {};
      (r.data.pilares || []).forEach((pl) => {
        const sem = pl.departamento_codigo === '__sem__';
        const cod = consolidar ? (sem ? '' : chaveNome(pl.departamento)) : (sem ? '' : String(pl.departamento_codigo || ''));
        const ant = real[cod] || { receita: 0, despesa: 0 };
        nomes[cod] = pl.departamento;
        real[cod] = { receita: ant.receita + (pl.receitas?.total || 0), despesa: ant.despesa + (pl.despesas?.total || 0) };
      });
      setNomesPilar(nomes); setRealizadoPilar(real);
    }).catch(() => {});
    return () => { vivo = false; };
  }, [clienteId, ano, baseFim, consolidar]);

  const usarValores = (natureza, codigo, lista) => {
    lista.forEach((x) => p.editar(natureza, codigo, x.mes, x.valor)); setCatAberta(null);
  };
  const salvar = async () => {
    const r = await p.salvar();
    notificar(r.ok ? `Orçamento gravado: ${r.gravados} células.` : (r.erro || 'Falha ao gravar'));
  };
  const ajustarDecisao = async (natureza, cod, desfazer) => {
    if (await p.marcarDecisao(natureza, cod, desfazer)) { setVersao((v) => v + 1); setCatAberta(null); }
  };
  const ajustarOutlier = async (natureza, cod, mes, rec) => {
    if (await p.ignorarOutlier(natureza, cod, mes, rec)) setVersao((v) => v + 1);
  };

  if (!p.proj && p.anoCoberto) {
    const fim = fimDoErro(p.erro);
    return (
      <div className="op-wrap pa-page">
        <div className="pa-card">
          <div className="pa-card-body d-flex align-items-center gap-3 flex-wrap">
            <i className="fas fa-circle-info" style={{ color: '#00205b' }}></i>
            <span>
              A base histórica vai até {fim || `${ano}`}. A projeção é para o ano seguinte.
            </span>
            {onTrocarAno && (
              <button className="pa-btn pa-btn-primary ms-auto" onClick={() => onTrocarAno(ano + 1)}>
                <i className="fas fa-forward me-1"></i>Projetar {ano + 1}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  if (!p.proj && p.erro) {
    return (
      <div className="alert alert-danger d-flex align-items-center gap-3 flex-wrap">
        <span>{p.erro}</span>
        {consolidar && (
          <button type="button" className="pa-btn pa-btn-ghost ms-auto" onClick={alternarConsolidar}>
            <i className="fas fa-layer-group me-1"></i>Sair da visão consolidada
          </button>
        )}
      </div>
    );
  }
  if (!p.proj) return <div className="text-muted py-4"><i className="fas fa-spinner fa-spin me-2"></i>Projetando {ano}… (10.000 simulações)</div>;

  const secoes = SECOES.map(([natureza, titulo]) => ({ natureza, titulo, categorias: categoriasDe(p.proj, natureza) }));
  // painel lateral sempre com a versão mais nova da categoria (selo muda após um ajuste)
  const catAtual = catAberta
    ? (categoriasDe(p.proj, catAberta.natureza).find((c) => c.codigo === catAberta.codigo) || catAberta.cat)
    : null;
  const res = p.resultado;
  const leitura = !!p.proj.somente_leitura;
  const siglas = p.proj.consolidado ? siglasDe(p.proj.clientes) : null;
  const nomesGrupo = (p.proj.clientes || []).map((c) => c.nome).join(' + ');
  // consolidado: a categoria abre o popover das origens (a análise é por empresa)
  const abrirCategoria = (natureza, c) => (leitura
    ? setOrigensAbertas({ natureza, cat: c })
    : setCatAberta({ natureza, codigo: c.codigo, cat: c }));
  // realizado do rodapé no consolidado: pilar (nome exibido) → chave normalizada do DRE consolidado
  const realizadoRodape = (leitura && realizadoPilar)
    ? Object.fromEntries(Object.keys(p.pilares).map((k) => [k, realizadoPilar[k ? chaveNome(k) : '']]).filter(([, v]) => v))
    : realizadoPilar;
  return (
    <div className="op-wrap pa-page">
      <ComoFunciona aberto={ajuda} onFechar={fecharAjuda} />
      <div className="op-tabs">
        <button className={`op-tab ${aba === 'orcamento' ? 'ativa' : ''}`} onClick={() => setAba('orcamento')}><i className="fas fa-table me-1"></i>Orçamento</button>
        <button className={`op-tab ${aba === 'simulacao' ? 'ativa' : ''}`} onClick={() => setAba('simulacao')}><i className="fas fa-dice me-1"></i>Simulação</button>
        <button className={`op-tab ${aba === 'premissas' ? 'ativa' : ''}`} onClick={() => setAba('premissas')}><i className="fas fa-sliders me-1"></i>Premissas</button>
        <button className="op-tab op-tab-ajuda" onClick={() => setAjuda(true)}><i className="fas fa-circle-question me-1"></i>Como funciona</button>
        {(p.proj.consolidavel || consolidar) && (
          <button type="button" className={`pa-btn pa-btn-toggle ${consolidar ? 'is-on' : ''}`} aria-pressed={consolidar}
            title="Somar a projeção desta empresa com a da outra empresa do grupo (somente leitura)"
            onClick={alternarConsolidar}>
            <i className="fas fa-layer-group me-1"></i>{consolidar && p.proj.consolidado ? `Consolidado: ${nomesGrupo}` : 'Consolidar empresas'}
          </button>
        )}
        {/* resumo da base, dos selos por natureza e do resultado do ano */}
        <div className="op-resumo ms-auto">
          <span title="Período de títulos usado para projetar (o mês corrente fica de fora por estar incompleto)">
            <i className="fas fa-history me-1"></i>Histórico usado: {mesAno(p.proj.base?.ini)} a {mesAno(p.proj.base?.fim)} ({p.proj.base?.meses} meses)
            {p.proj.consolidado && ` · ${(p.proj.clientes || []).length} empresas`}
          </span>
          {secoes.map((s) => <Selos key={s.natureza} titulo={s.titulo} selos={contarSelos(s.categorias)} />)}
          {/* resultado do ano nas simulações (já deslocado pelas edições da grade) */}
          <span className="op-resumo-p op-resumo-res"
            title="Resultado (receitas − despesas) nas 10.000 simulações, com receita e despesa do mesmo cenário. P10: só 10% dos cenários terminaram pior · P50: o do meio · P90: só 10% terminaram melhor">
            Resultado do ano: <span>P10 <b>{fmtMi(res.percentil(10))}</b></span>
            <span className="p50">P50 <b>{fmtMi(res.percentil(50))}</b></span>
            <span>P90 <b>{fmtMi(res.percentil(90))}</b></span>
          </span>
          <span className="op-resumo-p" title="P50 de cada lado, já com as suas edições">
            Receitas P50 <b>{fmtMi(p.receitas.percentil(50))}</b> · Despesas P50 <b>{fmtMi(p.despesas.percentil(50))}</b>
          </span>
        </div>
        <button className="pa-btn pa-btn-ghost" onClick={p.recarregar} disabled={p.carregando} title="Recalcular (ignora o cache)"><i className="fas fa-rotate"></i></button>
        {!leitura && (
          <button className="pa-btn pa-btn-primary" onClick={salvar} disabled={p.salvando || p.carregando}><i className="fas fa-save me-1"></i>Gravar orçamento</button>
        )}
      </div>
      {leitura && (
        <div className="op-consol-banner">
          <i className="fas fa-layer-group me-2"></i>Visão consolidada (somente leitura) · valores somados das duas empresas
          {siglas && <span className="ms-2">{Object.values(siglas).map((s) => <span key={s.sigla} className="op-emp" title={s.nome}>{s.sigla}</span>)}</span>}
        </div>
      )}
      {p.carregando && <div className="op-recalc"><i className="fas fa-spinner fa-spin me-2"></i>Recalculando…</div>}
      {p.erro && <div className="alert alert-danger py-2 small">{p.erro}</div>}
      {p.proj.avisos?.map((a) => <div key={a} className="alert alert-warning py-2 small">{a}</div>)}
      {aba === 'orcamento' && (
        <>
          <GridProjecao secoes={secoes} meses={p.meses} totais={p.totais} valorCelula={p.valorCelula}
            edicoes={p.edicoes} onEditar={p.editar}
            onAbrirCategoria={abrirCategoria} somenteLeitura={leitura} siglas={siglas} />
          <PainelPilares pilares={p.pilares} nomesPilar={leitura ? {} : nomesPilar} realizadoPorPilar={realizadoRodape}
            totais={p.totais} ano={ano} baseFim={baseFim} grupo={leitura} />
        </>
      )}
      {aba === 'simulacao' && (
        <AbaSimulacao clienteId={clienteId} proj={p.proj} resultado={p.resultado}
          receitas={p.receitas} despesas={p.despesas} probPrejuizo={p.probPrejuizo} chanceMeta={p.chanceMeta}
          pilaresSimulados={p.pilaresSimulados} composicaoPilar={p.composicaoPilar} onAbrirCategoria={abrirCategoria}
          consolidar={consolidar} siglas={siglas}
          deslocamento={p.totais.receita.deslocamento - p.totais.despesa.deslocamento} />
      )}
      {/* Premissas fica montada (só escondida) para não perder uma mudança ainda no debounce ao trocar de aba */}
      <div hidden={aba !== 'premissas'}>
        {leitura
          ? <PremissasConsolidado proj={p.proj} onAbrirEmpresa={abrirEmpresa} />
          : <AbaPremissas clienteId={clienteId} ano={ano} proj={p.proj} resultado={p.resultado}
              probPrejuizo={p.probPrejuizo} carregando={p.carregando}
              salvarPremissas={p.salvarPremissas} atualizar={p.atualizar} />}
      </div>
      {origensAbertas && (
        <OrigensCategoria alvo={origensAbertas} siglas={siglas} onAbrirEmpresa={abrirEmpresa} onFechar={fecharOrigens} />
      )}
      {/* painel da categoria: abre da grade (aba Orçamento) ou da composição do pilar (aba Simulação) */}
      {catAtual && !leitura && (
        <AnaliseCategoria clienteId={clienteId} ano={ano} natureza={catAberta.natureza} categoria={catAtual}
          meses={p.meses} versao={versao}
          onFechar={() => setCatAberta(null)} onUsarValores={usarValores}
          onDecisao={ajustarDecisao} onIgnorarOutlier={ajustarOutlier} />
      )}
    </div>
  );
}
